import { render } from "preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { marked } from "marked";
import { SubscribeScreen, PlanLine, TenantPlansAdmin, type BillingInfo } from "./billing.js";
import { Onboarding, WeeklyReportsAdmin, useOnboarding } from "./onboarding.js";
import { InboxView, usePendingCount, OVERSIGHT_CSS } from "./inbox.js";
import { TeamView } from "./team.js";
import { InquiriesPanel, useNewInquiryCount } from "./inquiries.js";

type Me = {
  staff: { id: string; email: string; name: string; role: "admin" | "staff" | "tenant" };
  budget: { staffSpent: number; staffBudget: number | null; teamSpent: number; teamBudget: number; anthropicExhausted: boolean; grsaiCredits: number };
  models: Array<{ id: string; label: string; provider: string; tier: string; tools: boolean }>;
  features: { images: boolean; jev: boolean; anthropic: boolean };
  skills: Array<{ name: string; description: string }>;
  downloads?: { mac: string; win: string };
  billing?: BillingInfo;
};
type EnvInfo = { desktop: boolean; cloud: string; workspace?: string; loggedIn?: boolean; previewUrl?: string | null };
type Pin = { n: number; selector: string; tag: string; text: string; page: string; comment: string };
type Chat = { id: string; title: string; mode: "auto" | "autonomous" | "agent" | "chat"; cwd?: string | null; updated_at: number };
let currentChatId = "";
const folderName = (p: string) => p.replace(/[\\/]+$/, "").split(/[\\/]/).pop() || p;
type Ev = { id?: number; kind: string; [k: string]: any };

async function api<T = any>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`/api${path}`, { headers: { "content-type": "application/json" }, ...init });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
}
const usd = (n: number | null | undefined) => (n == null ? "–" : `$${n.toFixed(n < 10 ? 2 : 0)}`);
const usd4 = (n: number | null | undefined) => (n == null ? "–" : n < 0.01 ? `$${n.toFixed(4)}` : `$${n.toFixed(3)}`);
const k = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

marked.setOptions({ breaks: true, gfm: true });
function Md({ text }: { text: string }) {
  const html = useMemo(() => {
    let h = marked.parse(text) as string;
    h = h.replace(/src="(?!https?:|data:|\/)([^"]+)"/g, (_m, p) => `src="/api/files/${p.replace(/^\.\//, "")}?chat=${currentChatId}"`);
    return h;
  }, [text]);
  return <div class="md" dangerouslySetInnerHTML={{ __html: html }} />;
}

// ---------------- Login
function Login({ onDone, env }: { onDone: () => void; env: EnvInfo }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [cloud, setCloud] = useState(env.cloud);
  const [showCloud, setShowCloud] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [choices, setChoices] = useState<Array<{ siteId: string; siteName: string }> | null>(null);
  const submit = async (e: Event | null, siteId?: string) => {
    e?.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const res = await fetch("/api/login", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ email, password, siteId, cloud: env.desktop && showCloud ? cloud : undefined }) });
      const j: any = await res.json().catch(() => ({}));
      if (res.status === 409 && j.reason === "choose_site") { setChoices(j.sites ?? []); return; }
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      onDone();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div class="login">
      <form class="card" onSubmit={submit}>
        <h1>Xelent <span style="color:var(--accent)">Agent</span></h1>
        <p>Company staff: the account your admin created. Website owners: the email and password of your Xelent Sites account — your website is connected automatically.{env.desktop ? " Your work stays on this computer." : ""}</p>
        {env.desktop && showCloud && <div class="field"><label>Company server</label><input class="input" value={cloud} onInput={(e) => setCloud((e.target as HTMLInputElement).value)} /></div>}
        <div class="field"><label>Email</label><input class="input" type="email" value={email} onInput={(e) => setEmail((e.target as HTMLInputElement).value)} required /></div>
        <div class="field"><label>Password</label><input class="input" type="password" value={password} onInput={(e) => setPassword((e.target as HTMLInputElement).value)} required /></div>
        {choices && (
          <div class="field">
            <label>Which website should this computer work on?</label>
            {choices.map((c) => <button type="button" class="btn" style="width:100%;margin-bottom:6px" disabled={busy} onClick={() => submit(null, c.siteId)}>{c.siteName}</button>)}
          </div>
        )}
        {err && <div class="error">{err}</div>}
        <button class="btn primary" style="width:100%" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</button>
        {env.desktop && <div class="hint" style="margin-top:10px;text-align:center;cursor:pointer" onClick={() => setShowCloud(!showCloud)}>{showCloud ? "Hide server" : "Change company server"}</div>}
      </form>
    </div>
  );
}

// ---------------- Chat rendering
type Item =
  | { t: "user"; text: string; images?: string[] }
  | { t: "assistant"; text: string; live?: boolean; model?: string | null }
  | { t: "tool"; id: string; name: string; input: any; result?: string; isError?: boolean; running: boolean }
  | { t: "perm"; id: string; tool: string; input: any; answered?: "allow" | "deny" }
  | { t: "result"; costUsd: number; durationMs: number; isError: boolean; errors?: string[] | null }
  | { t: "cost"; costUsd: number; credits: number; pkr?: number; inputTokens: number; outputTokens: number; models: string; chatTotalUsd: number; chatTotalPkr?: number }
  | { t: "error"; message: string };

function reduce(events: Ev[]): Item[] {
  const items: Item[] = [];
  const tools = new Map<string, Extract<Item, { t: "tool" }>>();
  for (const ev of events) {
    switch (ev.kind) {
      case "user":
        items.push({ t: "user", text: ev.text, images: ev.images });
        break;
      case "assistant": {
        if (ev.parentToolUseId) break; // subagent chatter stays hidden
        for (const b of ev.blocks ?? []) {
          if (b.type === "text" && b.text?.trim()) items.push({ t: "assistant", text: b.text, model: ev.model });
          if (b.type === "tool_use") {
            const it: Extract<Item, { t: "tool" }> = { t: "tool", id: b.id, name: b.name, input: b.input, running: true };
            tools.set(b.id, it);
            items.push(it);
          }
        }
        break;
      }
      case "tool_result": {
        const it = tools.get(ev.toolUseId);
        if (it) (it.result = ev.text, it.isError = ev.isError, it.running = false);
        break;
      }
      case "permission":
        items.push({ t: "perm", id: ev.id, tool: ev.tool, input: ev.input, answered: ev.answered });
        break;
      case "permission_answer": {
        const p = items.find((i) => i.t === "perm" && i.id === ev.id) as Extract<Item, { t: "perm" }> | undefined;
        if (p) p.answered = ev.allow ? "allow" : "deny";
        break;
      }
      case "result":
        for (const it of tools.values()) it.running = false;
        items.push({ t: "result", costUsd: ev.costUsd ?? 0, durationMs: ev.durationMs ?? 0, isError: ev.isError, errors: ev.errors });
        break;
      case "cost": {
        // the ledger line replaces the engine's own estimate for this turn
        const last = items[items.length - 1];
        if (last && last.t === "result") items.pop();
        items.push({ t: "cost", costUsd: ev.costUsd ?? 0, credits: ev.credits ?? 0, inputTokens: ev.inputTokens ?? 0, outputTokens: ev.outputTokens ?? 0, models: ev.models ?? "", chatTotalUsd: ev.chatTotalUsd ?? 0 });
        break;
      }
      case "error":
        items.push({ t: "error", message: ev.message });
        break;
    }
  }
  return items;
}

function toolSummary(name: string, input: any): string {
  if (!input) return "";
  if (name === "Bash") return input.command ?? "";
  if (name === "Read" || name === "Write" || name === "Edit") return input.file_path ?? "";
  if (name === "Glob" || name === "Grep") return input.pattern ?? "";
  if (name === "WebFetch" || name === "WebSearch") return input.url ?? input.query ?? "";
  if (name.endsWith("generate_image")) return input.prompt ?? "";
  if (name === "Skill") return input.skill ?? "";
  const s = JSON.stringify(input);
  return s.length > 140 ? s.slice(0, 140) + "…" : s;
}

function FileTree({ items, onOpen, depth = 0 }: { items: any[]; onOpen: (p: string) => void; depth?: number }) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  return (
    <ul class="tree" style={`padding-left:${depth ? 12 : 0}px`}>
      {items.map((it) => (
        <li>
          {it.dir ? (
            <>
              <span class="tree__dir" onClick={() => setOpen({ ...open, [it.path]: !open[it.path] })}>{open[it.path] ? "▾" : "▸"} {it.name}</span>
              {open[it.path] && it.children && <FileTree items={it.children} onOpen={onOpen} depth={depth + 1} />}
            </>
          ) : (
            <span class="tree__file" title="Open" onClick={() => onOpen(it.path)}>{it.name}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

function ToolCard({ it }: { it: Extract<Item, { t: "tool" }> }) {
  const [open, setOpen] = useState(false);
  const name = it.name.replace(/^mcp__xelent__/, "xelent:");
  return (
    <div class={`tool${it.isError ? " err" : ""}`}>
      <div class="head" onClick={() => setOpen(!open)}>
        <i class={`dot ${it.running ? "run" : it.isError ? "err" : "ok"}`} />
        <b>{name}</b>
        <span class="sum">{toolSummary(it.name, it.input)}</span>
        <span>{open ? "▾" : "▸"}</span>
      </div>
      {open && (
        <div class="body">
          {JSON.stringify(it.input, null, 2)}
          {it.result !== undefined && `\n\n— result —\n${it.result.slice(0, 6000)}${it.result.length > 6000 ? "\n…" : ""}`}
        </div>
      )}
    </div>
  );
}

function PermCard({ it, onAnswer }: { it: Extract<Item, { t: "perm" }>; onAnswer: (allow: boolean, always: boolean) => void }) {
  const detail = it.tool === "Bash" ? it.input?.command : it.input?.file_path ?? JSON.stringify(it.input);
  return (
    <div class="perm">
      <div><b>{it.tool}</b> wants to run:</div>
      <div class="cmd">{String(detail ?? "")}</div>
      {it.answered ? (
        <div class="hint">{it.answered === "allow" ? "Allowed" : "Denied"}</div>
      ) : (
        <div class="actions">
          <button class="btn primary small" onClick={() => onAnswer(true, false)}>Allow once</button>
          <button class="btn small" onClick={() => onAnswer(true, true)}>Always allow</button>
          <button class="btn small danger" onClick={() => onAnswer(false, false)}>Deny</button>
        </div>
      )}
    </div>
  );
}

function ChatView({ me, chat, onTitle, env, onTurnEnd, autoSend, onAutoSent }: { me: Me; chat: Chat; onTitle: (t: string) => void; env: EnvInfo; onTurnEnd?: () => void; autoSend?: string; onAutoSent?: () => void }) {
  const [events, setEvents] = useState<Ev[]>([]);
  const [live, setLive] = useState("");
  const [running, setRunning] = useState(false);
  const [text, setText] = useState("");
  const [model, setModel] = useState("");
  const [mode, setMode] = useState<Chat["mode"]>(env.desktop ? chat.mode : "chat");
  const [err, setErr] = useState("");
  const [activity, setActivity] = useState("");
  const [queued, setQueued] = useState<string[]>([]);
  const [attached, setAttached] = useState<Array<{ name: string; mime: string; data: string; url: string }>>([]);
  // ---- preview pane + comment mode
  const [showPreview, setShowPreview] = useState(false);
  const [target, setTarget] = useState("folder");
  const [urlDraft, setUrlDraft] = useState("http://localhost:5173");
  const [frameKey, setFrameKey] = useState(0);
  const [commentMode, setCommentMode] = useState(false);
  const [pins, setPins] = useState<Pin[]>([]);
  const [pagePath, setPagePath] = useState("");
  const frame = useRef<HTMLIFrameElement>(null);
  const postToFrame = (msg: any) => frame.current?.contentWindow?.postMessage(msg, "*");
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      const d = e.data || {};
      if (d.type === "xelent-ready") {
        setPagePath(d.page || "");
        postToFrame({ type: "xelent-mode", on: commentMode });
      } else if (d.type === "xelent-pick") {
        const comment = prompt(`Comment on <${d.tag}>${d.text ? ` "${d.text.slice(0, 60)}"` : ""}:`);
        if (comment === null) return;
        setPins((p) => [...p, { n: d.n, selector: d.selector, tag: d.tag, text: d.text, page: d.page, comment }]);
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [commentMode]);
  useEffect(() => {
    postToFrame({ type: "xelent-mode", on: commentMode });
  }, [commentMode]);
  const clearPins = () => {
    setPins([]);
    postToFrame({ type: "xelent-clear" });
  };
  const sendPins = () => {
    if (!pins.length) return;
    const page = pins[0].page || pagePath || "/";
    const lines = pins.map((p) => `${p.n}. ${p.comment}\n   element: <${p.tag}>${p.text ? ` "${p.text.slice(0, 80)}"` : ""} — selector \`${p.selector}\``);
    send(`Please make these changes on the page ${page}${target !== "folder" ? ` (served at ${target})` : ""}:\n${lines.join("\n")}\nKeep everything else as it is, then tell me what you changed.`);
    clearPins();
    setCommentMode(false);
  };
  const previewSrc = env.previewUrl ? `${env.previewUrl}/__xelent/target?chat=${chat.id}&target=${encodeURIComponent(target)}` : "";
  const addFiles = (files: FileList | File[] | null) => {
    if (!files) return;
    for (const f of Array.from(files)) {
      if (!/^image\/(png|jpeg|webp|gif)$/.test(f.type) || f.size > 8_000_000) continue;
      const r = new FileReader();
      r.onload = () => setAttached((a) => (a.length >= 6 ? a : [...a, { name: f.name || "pasted.png", mime: f.type, data: String(r.result), url: URL.createObjectURL(f) }]));
      r.readAsDataURL(f);
    }
  };
  const scroller = useRef<HTMLDivElement>(null);
  const ta = useRef<HTMLTextAreaElement>(null);

  const [folder, setFolder] = useState<string>(chat.cwd || env.workspace || "");
  const [showFiles, setShowFiles] = useState(false);
  const [tree, setTree] = useState<any[] | null>(null);
  const loadTree = () => api(`/workspace?chat=${chat.id}`).then((r) => { setTree(r.tree); setFolder(r.root); }).catch(() => setTree([]));
  useEffect(() => {
    if (showFiles) loadTree();
  }, [showFiles, chat.id]);
  const changeFolder = async () => {
    const r = await api("/folder/pick", { method: "POST" }).catch((e) => ({ error: e.message }));
    if (r.error) return alert(r.error);
    if (!r.path) return;
    await api(`/chats/${chat.id}`, { method: "PATCH", body: JSON.stringify({ cwd: r.path }) });
    setFolder(r.path);
    if (showFiles) loadTree();
  };
  const openFolder = (p?: string) => api("/folder/open", { method: "POST", body: JSON.stringify({ path: p || folder }) }).catch((e) => alert(e.message));
  useEffect(() => {
    currentChatId = chat.id;
    setFolder(chat.cwd || env.workspace || "");
    setEvents([]);
    setLive("");
    setMode(env.desktop ? chat.mode : "chat");
    api(`/chats/${chat.id}`).then((r) => {
      setEvents((r.events as any[]).map((e) => ({ ...(e.payload ?? {}), id: e.id, ts: e.ts, kind: e.kind })));
      setRunning(r.running);
      if (autoSend && !r.events.length) {
        onAutoSent?.();
        send(autoSend); // a "Start here" prompt: runs as soon as its new chat has loaded
      }
    });
  }, [chat.id]);
  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight });
  }, [events, live]);

  const items = useMemo(() => reduce(events), [events]);
  useEffect(() => {
    if (!running && queued.length) {
      const [next, ...rest] = queued;
      setQueued(rest);
      send(next);
    }
  }, [running, queued]);

  async function send(t?: string) {
    const body = (t ?? text).trim();
    if (!body && !attached.length) return;
    if (running) {
      // queued like Claude Code: delivered as the next message once this turn ends
      setQueued((q) => [...q, body]);
      setText("");
      return;
    }
    setText("");
    setActivity("");
    setErr("");
    setRunning(true);
    if (events.length === 0) onTitle(body.slice(0, 60));
    if (!env.desktop) setEvents((p) => [...p, { kind: "user", text: body }]);
    try {
      const imgs = attached.map((a) => ({ name: a.name, mime: a.mime, data: a.data }));
      setAttached([]);
      const res = await fetch(`/api/chats/${chat.id}/send`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ text: body || "See the attached image.", model: model || undefined, images: imgs.length ? imgs : undefined }) });
      if (!res.ok || !res.body) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      let liveText = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let idx: number;
        while ((idx = buf.indexOf("\n\n")) >= 0) {
          const chunk = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const data = chunk.split("\n").filter((l) => l.startsWith("data:")).map((l) => l.slice(5).trim()).join("");
          if (!data) continue;
          let ev: Ev;
          try {
            ev = JSON.parse(data);
          } catch {
            continue;
          }
          if (ev.chatId && ev.chatId !== chat.id) continue;
          if (ev.kind === "delta") {
            liveText += ev.text;
            setLive(liveText);
          } else if (ev.kind === "assistant") {
            liveText = "";
            setLive("");
            setEvents((p) => [...p, ev]);
          } else if (ev.kind === "turn_end") {
            setLive("");
          } else if (ev.kind === "tool_start" || ev.kind === "status") {
            if (ev.text) setActivity(String(ev.text));
          } else if (ev.kind === "message_start" || ev.kind === "ping") {
            // keep-alive only
          } else {
            if (ev.kind === "result" || ev.kind === "cost") setActivity("");
            setEvents((p) => [...p, ev]);
          }
        }
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setRunning(false);
      setLive("");
      setActivity("");
      onTurnEnd?.();
      if (showFiles) loadTree();
    }
  }

  async function answer(id: string, allow: boolean, always: boolean) {
    await api(`/chats/${chat.id}/permission`, { method: "POST", body: JSON.stringify({ id, allow, always }) });
    setEvents((p) => [...p, { kind: "permission_answer", id, allow }]);
  }
  async function stop() {
    await api(`/chats/${chat.id}/interrupt`, { method: "POST" });
  }
  async function changeMode(m: Chat["mode"]) {
    setMode(m);
    await api(`/chats/${chat.id}`, { method: "PATCH", body: JSON.stringify({ mode: m }) });
  }

  const suggestions = [
    "Build a one-page website for a Sialkot sportswear exporter with a hero, products and contact form",
    "Generate a hero image: a modern sublimated football kit on a white studio background",
    "Write 5 Instagram captions for a new BJJ gi launch",
    "Review the files in my workspace and list what's there",
  ];

  return (
    <>
      {env.desktop && (
        <div class="folderbar">
          <span class="hint">Working in</span>
          <b title={folder}>{folderName(folder)}</b>
          <button class="btn small" onClick={changeFolder} disabled={running}>Change folder…</button>
          <button class="btn small" onClick={() => openFolder()}>Open in {navigator.platform.startsWith("Mac") ? "Finder" : "Explorer"}</button>
          <span class="spacer" />
          <button class={`btn small${showFiles ? " primary" : ""}`} onClick={() => setShowFiles(!showFiles)}>Files</button>
          {env.previewUrl && <button class={`btn small${showPreview ? " primary" : ""}`} onClick={() => setShowPreview(!showPreview)}>Preview</button>}
        </div>
      )}
      <div class="chatbody">
      {showFiles && (
        <aside class="files">
          <div class="files__head"><b title={folder}>{folderName(folder)}</b><button class="btn small" onClick={loadTree}>↻</button></div>
          {tree === null ? <div class="hint">Loading…</div> : tree.length === 0 ? <div class="hint">Empty folder. Ask the agent to create something, or drop files here in Finder.</div> : <FileTree items={tree} onOpen={(p) => openFolder(p)} />}
        </aside>
      )}
      <div class="msgs" ref={scroller}>
        {items.length === 0 && !live && (
          <div class="empty">
            <h2>What are we building, {me.staff.name.split(" ")[0]}?</h2>
            {env.desktop ? (
              <div>Files, commands, websites, images and copy. This chat works in <code>{folder}</code>; use "Change folder…" above to point it at a project.</div>
            ) : (
              <div>
                This is chat mode in the browser. For files, commands, websites and images, install the desktop app:
                {me.downloads?.mac ? <> <a href={me.downloads.mac}>Mac</a></> : null}{me.downloads?.win ? <> · <a href={me.downloads.win}>Windows</a></> : null}
                {!me.downloads?.mac && !me.downloads?.win ? " (ask your admin for the download link)" : ""}
              </div>
            )}
            <div class="chips">{suggestions.map((s) => <span class="chip" onClick={() => send(s)}>{s}</span>)}</div>
          </div>
        )}
        {items.map((it, i) => {
          if (it.t === "user") return <div class="msg user" key={i}><div class="bubble">{it.images?.length ? <div class="attach-row">{it.images.map((p) => <img src={`/api/files/${p}?chat=${chat.id}`} />)}</div> : null}{it.text}</div></div>;
          if (it.t === "assistant") return <div class="msg assistant" key={i}><Md text={it.text} /></div>;
          if (it.t === "tool") return <div class="msg" key={it.id}><ToolCard it={it} /></div>;
          if (it.t === "perm") return <div class="msg" key={it.id}><PermCard it={it} onAnswer={(a, al) => answer(it.id, a, al)} /></div>;
          if (it.t === "result") return <div class="result" key={i}>{it.isError ? `Stopped: ${(it.errors ?? []).join("; ") || "error"}` : `Done · ${(it.durationMs / 1000).toFixed(0)}s · ~${usd(it.costUsd)}`}</div>;
          if (it.t === "cost" && me.staff.role === "tenant") return <div class="result" key={i}>{(it.pkr ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} credits · chat total {(it.chatTotalPkr ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} credits</div>;
          if (it.t === "cost") return <div class="result" key={i} title={it.models}>Cost {usd4(it.costUsd)} · {k(it.inputTokens)} in / {k(it.outputTokens)} out{it.models ? ` · ${it.models.split(",").join(", ")}` : ""}{it.credits && me.staff.role === "admin" ? ` · ${Math.round(it.credits)} credits` : ""} · chat total {usd4(it.chatTotalUsd)}</div>;
          if (it.t === "error") return <div class="msg" key={i}><div class="error">{it.message}</div></div>;
          return null;
        })}
        {live && <div class="msg assistant"><Md text={live} /></div>}
        {running && !live && <div class="result"><i class="dot run" style="display:inline-block;margin-right:6px" />{activity || "working…"}</div>}
        {running && live && activity && <div class="result">{activity}</div>}
        {queued.map((q, i) => <div class="msg user" key={`q${i}`}><div class="bubble" style="opacity:.55" title="Queued: sent when the current turn ends">{q}</div></div>)}
      </div>
      {showPreview && env.previewUrl && (
        <aside class="preview">
          <div class="preview__bar">
            <select value={target === "folder" ? "folder" : "url"} onChange={(e) => setTarget((e.target as HTMLSelectElement).value === "folder" ? "folder" : urlDraft)}>
              <option value="folder">Chat folder</option>
              <option value="url">Local server</option>
            </select>
            {target !== "folder" && <input class="input" style="padding:4px 8px;font-size:12px;width:170px" value={urlDraft} onInput={(e) => setUrlDraft((e.target as HTMLInputElement).value)} onKeyDown={(e) => { if (e.key === "Enter") setTarget(urlDraft); }} />}
            <button class="btn small" title="Reload" onClick={() => setFrameKey((k) => k + 1)}>↻</button>
            <button class={`btn small${commentMode ? " primary" : ""}`} onClick={() => setCommentMode(!commentMode)} title="Click elements in the page to pin comments">{commentMode ? "Commenting…" : "Comment"}</button>
            <span class="spacer" />
            <span class="hint" title={pagePath}>{pagePath}</span>
          </div>
          <iframe key={`${frameKey}-${target}`} ref={frame} src={previewSrc} class="preview__frame" />
          {pins.length > 0 && (
            <div class="preview__pins">
              {pins.map((p) => <div class="pin"><b>{p.n}</b> <span>{p.comment}</span> <code>{p.tag}{p.text ? ` "${p.text.slice(0, 40)}"` : ""}</code></div>)}
              <div style="display:flex;gap:6px;margin-top:6px"><button class="btn primary small" onClick={sendPins}>Send {pins.length} comment{pins.length > 1 ? "s" : ""} to agent</button><button class="btn small" onClick={clearPins}>Clear</button></div>
            </div>
          )}
        </aside>
      )}
      </div>
      <div class="composer">
        <div class="box">
          {attached.length > 0 && (
            <div class="attach-row">
              {attached.map((a, i) => <span class="attach"><img src={a.url} /><button class="attach__x" onClick={() => setAttached(attached.filter((_, j) => j !== i))}>✕</button></span>)}
            </div>
          )}
          <textarea
            ref={ta}
            onPaste={(e) => { const items = Array.from(e.clipboardData?.items ?? []).filter((it) => it.kind === "file").map((it) => it.getAsFile()!).filter(Boolean); if (items.length) { e.preventDefault(); addFiles(items); } }}
            onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer?.files ?? null); }}
            onDragOver={(e) => e.preventDefault()}
            placeholder={mode === "chat" || !env.desktop ? "Ask or write anything (no file access in chat mode)…" : "Tell the agent what to do… (Enter to send, Shift+Enter for a new line)"}
            value={text}
            onInput={(e) => {
              const el = e.target as HTMLTextAreaElement;
              setText(el.value);
              el.style.height = "auto";
              el.style.height = Math.min(el.scrollHeight, 240) + "px";
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
          />
          <div class="row">
            {env.desktop ? (
              <select value={mode} onChange={(e) => changeMode((e.target as HTMLSelectElement).value as Chat["mode"])} title="Mode">
                <option value="auto">Agent (asks before commands)</option>
                <option value="autonomous">Agent (autonomous, safety-checked)</option>
                <option value="chat">Chat only (cheapest)</option>
              </select>
            ) : (
              <span>Chat only · web</span>
            )}
            {me.staff.role === "admin" && (
              <select value={model} onChange={(e) => setModel((e.target as HTMLSelectElement).value)} title="Model">
                <option value="">Model: automatic</option>
                {me.models.map((m) => <option value={m.id}>{m.label}{m.tools ? "" : " · chat only"}</option>)}
              </select>
            )}
            {env.desktop && <label class="btn small" title="Attach images (or paste / drop them)">📎<input type="file" accept="image/*" multiple style="display:none" onChange={(e) => { addFiles((e.target as HTMLInputElement).files); (e.target as HTMLInputElement).value = ""; }} /></label>}
            <span class="spacer" />
            {err && <span class="error">{err}</span>}
            {running ? <><button class="btn small" onClick={() => send()} disabled={!text.trim()} title="Queue for after this turn">Queue</button><button class="btn small danger" onClick={stop}>Stop</button></> : <button class="btn primary small" onClick={() => send()} disabled={!text.trim()}>Send</button>}
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------- Admin
function Admin({ me, refresh }: { me: Me; refresh: () => void }) {
  const [tab, setTab] = useState<"settings" | "staff" | "models" | "usage" | "pricing" | "skills">("settings");
  return (
    <div class="admin">
      <h2>Admin</h2>
      <div class="tabs">{(["settings", "staff", "models", "usage", "pricing", "skills"] as const).map((t) => <span class={`tab${tab === t ? " active" : ""}`} onClick={() => setTab(t)}>{t[0].toUpperCase() + t.slice(1)}</span>)}</div>
      {tab === "settings" && <Settings onSaved={refresh} />}
      {tab === "staff" && <StaffAdmin />}
      {tab === "models" && <ModelsAdmin />}
      {tab === "usage" && <UsageAdmin />}
      {tab === "pricing" && <PricingAdmin />}
      {tab === "skills" && <SkillsAdmin me={me} />}
    </div>
  );
}

const SETTING_LABELS: Record<string, [string, string]> = {
  tenant_plan_price_pkr: ["Customer plan price (PKR / month)", "What website owners pay for the agent by card (Stripe Checkout). Used when no Stripe price id is set below."],
  tenant_plan_credits: ["Customer plan credits (per month)", "Credits included in the plan, 1 credit = PKR 1. Renew with each paid month (or on the 1st without a card plan)."],
  plan_enforcement: ["Plan enforcement (1/0)", "1 = website owners without a paid or granted month see the Subscribe screen instead of chat. 0 = nobody is asked to pay (default)."],
  stripe_secret_key: ["Stripe secret key", "sk_live_… (or sk_test_… to try it). From dashboard.stripe.com → Developers → API keys. Stored encrypted."],
  stripe_webhook_secret: ["Stripe webhook signing secret", "whsec_… from the webhook endpoint for https://agent.ayzelify.com/api/billing/webhook. Stored encrypted."],
  stripe_price_id: ["Stripe price id (optional)", "price_… for a monthly PKR price. Blank = the plan price above, billed monthly in PKR."],
  tenant_image_credits: ["Credits per image (standard)", "Charged to a website owner for one generated image on the standard model."],
  tenant_image_pro_credits: ["Credits per image (pro models)", "Charged for one image on the pro models listed below."],
  tenant_image_pro_models: ["Pro image models", "Comma-separated model ids billed at the pro image price (default nano-banana-pro)."],
  tenant_token_markup: ["Token markup for customers", "Multiplier on the real PKR cost of model tokens charged to website owners (1 = at cost)."],
  tenant_social_post_credits: ["Credits per social post", "Charged to a website owner for each post the agent schedules on their social networks (social_schedule_post, social_post_product)."],
  tenant_login_enabled: ["Website owners may sign in", "1 = owners of Xelent Sites websites can use the agent with their platform account; 0 = staff only."],
  platform_service_token: ["Platform service credential", "xsvc_… credential that lets this cloud connect staff and website owners to Xelent Sites without codes."],
  anthropic_api_key: ["Anthropic API key", "Pays for Claude Haiku / Sonnet / Opus. The only key that can drive the agent (files, commands)."],
  anthropic_monthly_budget_usd: ["Anthropic monthly budget (USD)", "Team-wide cap, e.g. 200. Resets on the 1st (UTC)."],
  anthropic_budget_mode: ["When the Anthropic budget is spent", "soft = agent work drops to Haiku; hard = agent work pauses, chat-only on Grsai continues"],
  staff_default_budget_usd: ["Default per-staff monthly budget (USD)", "Blank = no per-person cap. Override per person in Staff."],
  grsai_api_key: ["Grsai API key", "Gemini / GPT models for chat-only turns and all image generation."],
  grsai_base_url: ["Grsai base URL", "https://grsaiapi.com (global) or https://grsai.dakka.com.cn (China)"],
  grsai_monthly_credits: ["Grsai monthly credit cap", "Blank = no cap"],
  usd_pkr_rate_manual: ["USD → PKR rate (optional)", "Blank = live market rate, refreshed every 6 hours. Set a number (e.g. 281) to use your own rate for all PKR costs."],
  grsai_credit_usd: ["USD value of 1 Grsai credit", "Grsai's own credits: about $0.065 per 1200 credits → 0.000054. (Not the PKR credits of the Xelent API resale.)"],
  route_chat_to_grsai: ["Send tool-free turns to Grsai (1/0)", "Writing, Q&A and Claude Code's own side-calls go to Gemini Flash/Pro to save Anthropic tokens."],
  openrouter_api_key: ["OpenRouter API key (for Jev)", "Enables TypeSafe Jev as the real-time router. Blank = built-in heuristic router."],
  jev_model: ["Jev model id", "typesafe/jev-1.14 or ~typesafe/jev-latest"],
  default_tier: ["Default tier when unsure", "light | standard | heavy"],
  image_model: ["Default image model", "nano-banana-pro (≈1800 credits, 4K)"],
  image_model_alt: ["Alternative image model", "gpt-image-2.5 for strong text / exact re-renders"],
  company_context: ["Company context for every agent", "Brand facts, house rules, what staff build. Appended to the system prompt."],
};

function Settings({ onSaved }: { onSaved: () => void }) {
  const [s, setS] = useState<Record<string, { value: string; source: string; secret: boolean }> | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState("");
  useEffect(() => {
    api("/admin/settings").then((r) => setS(r.settings));
  }, []);
  if (!s) return <div class="hint">Loading…</div>;
  const save = async () => {
    const r = await api("/admin/settings", { method: "PUT", body: JSON.stringify(draft) });
    setS(r.settings);
    setDraft({});
    setMsg("Saved");
    onSaved();
    setTimeout(() => setMsg(""), 2000);
  };
  return (
    <div>
      <div class="form">
        {Object.keys(SETTING_LABELS).map((k) => {
          const [label, hint] = SETTING_LABELS[k];
          const cur = s[k];
          const wide = k === "company_context";
          return (
            <div class={`field${wide ? " wide" : ""}`}>
              <label>{label}<span class="src">{cur.source === "env" ? "(from .env)" : cur.source === "db" ? "" : "(default)"}</span></label>
              {wide ? (
                <textarea class="input" rows={6} value={draft[k] ?? cur.value} onInput={(e) => setDraft({ ...draft, [k]: (e.target as HTMLTextAreaElement).value })} />
              ) : (
                <input class="input" type={cur.secret ? "password" : "text"} placeholder={cur.secret && cur.value ? cur.value : ""} value={draft[k] ?? (cur.secret ? "" : cur.value)} onInput={(e) => setDraft({ ...draft, [k]: (e.target as HTMLInputElement).value })} />
              )}
              <span class="hint">{hint}</span>
            </div>
          );
        })}
      </div>
      <div style="margin-top:16px;display:flex;gap:10px;align-items:center">
        <button class="btn primary" onClick={save} disabled={!Object.keys(draft).length}>Save settings</button>
        <span class="hint">{msg}</span>
      </div>
      <AutomationToken />
      <WeeklyReportsAdmin />
    </div>
  );
}

function AutomationToken() {
  const [has, setHas] = useState<boolean | null>(null);
  const [token, setToken] = useState("");
  const [msg, setMsg] = useState("");
  useEffect(() => {
    api("/admin/settings").then((r) => setHas(!!r.automationToken));
  }, []);
  const gen = async () => {
    if (has && !confirm("Generate a new token? The old one stops working.")) return;
    const r = await api("/admin/automation-token", { method: "POST" });
    setToken(r.token);
    setHas(true);
    setMsg("");
  };
  const copy = () => navigator.clipboard.writeText(token).then(() => setMsg("Copied")).catch(() => setMsg("Select and copy it manually"));
  const revoke = async () => {
    if (!confirm("Revoke the automation token?")) return;
    await api("/admin/automation-token", { method: "DELETE" });
    setHas(false);
    setToken("");
  };
  return (
    <div style="margin-top:28px;border-top:1px solid var(--line);padding-top:16px">
      <h3 style="margin:0 0 6px">Automation token</h3>
      <p class="hint">Lets a script or Claude act as an admin (upload installers, skills, read usage) without your password. Shown once; generate again to revoke. {has === null ? "" : has ? "A token is active." : "No token yet."}</p>
      <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap">
        <button class="btn small" onClick={gen}>{has ? "Generate new token" : "Generate token"}</button>
        {has && <button class="btn small danger" onClick={revoke}>Revoke</button>}
        {token && <code style="background:var(--bg3);padding:6px 8px;border-radius:6px;font-size:12px;word-break:break-all">{token}</code>}
        {token && <button class="btn small" onClick={copy}>Copy</button>}
        <span class="hint">{msg}</span>
      </div>
    </div>
  );
}

function StaffAdmin() {
  const [rows, setRows] = useState<any[]>([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff", monthly_budget_usd: "" });
  const [err, setErr] = useState("");
  const load = () => api("/admin/staff").then((r) => setRows(r.staff));
  useEffect(() => {
    load();
  }, []);
  const add = async (e: Event) => {
    e.preventDefault();
    setErr("");
    try {
      await api("/admin/staff", { method: "POST", body: JSON.stringify({ ...form, monthly_budget_usd: form.monthly_budget_usd ? Number(form.monthly_budget_usd) : null }) });
      setForm({ name: "", email: "", password: "", role: "staff", monthly_budget_usd: "" });
      load();
    } catch (e) {
      setErr((e as Error).message);
    }
  };
  const patch = async (id: string, body: any) => {
    await api(`/admin/staff/${id}`, { method: "PATCH", body: JSON.stringify(body) });
    load();
  };
  return (
    <div>
      <form class="form" onSubmit={add} style="margin-bottom:22px">
        <div class="field"><label>Name</label><input class="input" value={form.name} onInput={(e) => setForm({ ...form, name: (e.target as HTMLInputElement).value })} required /></div>
        <div class="field"><label>Email</label><input class="input" type="email" value={form.email} onInput={(e) => setForm({ ...form, email: (e.target as HTMLInputElement).value })} required /></div>
        <div class="field"><label>Password (8+)</label><input class="input" value={form.password} onInput={(e) => setForm({ ...form, password: (e.target as HTMLInputElement).value })} required /></div>
        <div class="field"><label>Role</label><select class="input" value={form.role} onChange={(e) => setForm({ ...form, role: (e.target as HTMLSelectElement).value })}><option value="staff">staff</option><option value="admin">admin</option><option value="tenant">website owner</option></select></div>
        <div class="field"><label>Monthly budget USD (blank = default)</label><input class="input" value={form.monthly_budget_usd} onInput={(e) => setForm({ ...form, monthly_budget_usd: (e.target as HTMLInputElement).value })} /></div>
        <div class="field"><label>&nbsp;</label><button class="btn primary">Add staff member</button>{err && <div class="error">{err}</div>}</div>
      </form>
      <table class="grid">
        <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Budget</th><th>Spent this month</th><th>Active</th><th></th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr>
              <td>{r.name}</td><td>{r.email}</td><td>{r.role}</td>
              <td><input value={r.monthly_budget_usd ?? ""} placeholder="default" onChange={(e) => patch(r.id, { monthly_budget_usd: (e.target as HTMLInputElement).value ? Number((e.target as HTMLInputElement).value) : null })} /></td>
              <td>{usd(r.month_cost_usd)}</td>
              <td><input type="checkbox" checked={r.active} onChange={(e) => patch(r.id, { active: (e.target as HTMLInputElement).checked })} /></td>
              <td style="white-space:nowrap">
                <button class="btn small" onClick={() => { const p = prompt(`New password for ${r.name} (8+ chars)`); if (p && p.length >= 8) patch(r.id, { password: p }); }}>Reset password</button>{" "}
                <button class="btn small" onClick={() => patch(r.id, { rotate_key: true })} title="Rotate the gateway key">Rotate key</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ModelsAdmin() {
  const [models, setModels] = useState<any[]>([]);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    api("/admin/models").then((r) => setModels(r.models));
  }, []);
  const set = (i: number, k: string, v: any) => setModels(models.map((m, j) => (j === i ? { ...m, [k]: v } : m)));
  const save = async () => {
    try {
      const r = await api("/admin/models", { method: "PUT", body: JSON.stringify({ models }) });
      setModels(r.models);
      setMsg("Saved");
    } catch (e) {
      setMsg((e as Error).message);
    }
    setTimeout(() => setMsg(""), 2500);
  };
  const reset = async () => {
    const r = await api("/admin/models");
    setModels(r.defaults);
  };
  return (
    <div>
      <p class="hint">Tiers: light = quick answers and side-calls · standard = everyday work · heavy = hard tasks. Only Anthropic models can run tools; Grsai models serve tool-free turns. Prices are USD per million tokens and feed the ledger and budgets. Grsai ids must match <a href="https://grsai.com/dashboard/models" target="_blank">grsai.com/dashboard/models</a>.</p>
      <table class="grid">
        <thead><tr><th>On</th><th>Model id</th><th>Label</th><th>Provider</th><th>Tier</th><th>Tools</th><th>$ in /M</th><th>$ out /M</th><th>$ cache read /M</th><th></th></tr></thead>
        <tbody>
          {models.map((m, i) => (
            <tr>
              <td><input type="checkbox" checked={m.enabled} onChange={(e) => set(i, "enabled", (e.target as HTMLInputElement).checked)} /></td>
              <td><input value={m.id} onInput={(e) => set(i, "id", (e.target as HTMLInputElement).value)} /></td>
              <td><input value={m.label} onInput={(e) => set(i, "label", (e.target as HTMLInputElement).value)} /></td>
              <td><select value={m.provider} onChange={(e) => set(i, "provider", (e.target as HTMLSelectElement).value)}><option>anthropic</option><option>grsai</option></select></td>
              <td><select value={m.tier} onChange={(e) => set(i, "tier", (e.target as HTMLSelectElement).value)}><option>light</option><option>standard</option><option>heavy</option></select></td>
              <td>{m.provider === "anthropic" ? "yes" : "no"}</td>
              <td><input type="number" step="0.01" value={m.inputPerM} onInput={(e) => set(i, "inputPerM", Number((e.target as HTMLInputElement).value))} /></td>
              <td><input type="number" step="0.01" value={m.outputPerM} onInput={(e) => set(i, "outputPerM", Number((e.target as HTMLInputElement).value))} /></td>
              <td><input type="number" step="0.01" value={m.cacheReadPerM ?? ""} onInput={(e) => set(i, "cacheReadPerM", (e.target as HTMLInputElement).value === "" ? undefined : Number((e.target as HTMLInputElement).value))} /></td>
              <td><button class="btn small danger" onClick={() => setModels(models.filter((_, j) => j !== i))}>remove</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style="margin-top:12px;display:flex;gap:8px;align-items:center">
        <button class="btn small" onClick={() => setModels([...models, { id: "", label: "", provider: "grsai", tier: "standard", tools: false, inputPerM: 1, outputPerM: 5, enabled: true }])}>Add model</button>
        <button class="btn primary small" onClick={save}>Save models</button>
        <button class="btn small" onClick={reset}>Load defaults</button>
        <span class="hint">{msg}</span>
      </div>
    </div>
  );
}

function UsageAdmin() {
  const [d, setD] = useState<any>(null);
  const [task, setTask] = useState<any>(null);
  const load = () => api("/admin/usage").then(setD);
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);
  if (!d) return <div class="hint">Loading…</div>;
  const name = (id: string | null) => d.staff.find((s: any) => s.id === id)?.name ?? "—";
  const totalMonth = d.breakdown.reduce((a: number, r: any) => a + r.cost_usd, 0);
  const openTask = (id: string) => api(`/admin/usage/chat/${id}`).then(setTask);
  return (
    <div>
      <TenantPlansAdmin />
      <p class="hint">Live: refreshes every 15 s from the gateway ledger. Every model call, Jev decision and image is one row; a task = one chat, a turn = one message you sent.</p>
      <div class="kpis">
        <div class="kpi"><b>{usd4(d.today.cost_usd)}</b><span>Today, all providers ({d.today.requests} calls)</span></div>
        <div class="kpi"><b>{usd(totalMonth)}</b><span>This month, all providers</span></div>
        <div class="kpi"><b>{usd(d.budget.anthropicSpent)} / {usd(d.budget.anthropicBudget)}</b><span>Anthropic this month</span></div>
        <div class="kpi"><b>{Math.round(d.budget.grsaiCredits).toLocaleString()}</b><span>Grsai credits this month (LLM + images)</span></div>
        <div class="kpi"><b>{d.breakdown.reduce((a: number, r: any) => a + r.requests, 0)}</b><span>Requests this month</span></div>
      </div>
      <h3>Tasks (chats) by latest activity</h3>
      <table class="grid">
        <thead><tr><th>Task</th><th>Staff</th><th>Turns</th><th>Calls</th><th>Cost</th><th>Credits</th><th>Last activity</th></tr></thead>
        <tbody>{d.tasks.map((t: any) => <tr style="cursor:pointer" onClick={() => openTask(t.chat_id)}><td><code>{t.chat_id}</code></td><td>{name(t.staff_id)}</td><td>{t.turns}</td><td>{t.requests}</td><td>{usd4(t.cost_usd)}</td><td>{Math.round(t.credits)}</td><td>{new Date(t.last_ts).toLocaleString()}</td></tr>)}</tbody>
      </table>
      {task && (
        <div style="margin:14px 0 22px">
          <h3>Task {task.total.chat_id}: {usd4(task.total.cost_usd)} · {k(task.total.input_tokens)} in / {k(task.total.output_tokens)} out</h3>
          <table class="grid">
            <thead><tr><th>Turn</th><th>Calls</th><th>In</th><th>Out</th><th>Cost</th><th>Models</th></tr></thead>
            <tbody>{task.turns.map((t: any) => <tr><td><code>{t.turn_id ?? "—"}</code></td><td>{t.requests}</td><td>{k(t.input_tokens)}</td><td>{k(t.output_tokens)}</td><td>{usd4(t.cost_usd)}</td><td>{t.models}</td></tr>)}</tbody>
          </table>
        </div>
      )}
      <h3 style="margin-top:22px">By staff and model, this month</h3>
      <table class="grid">
        <thead><tr><th>Staff</th><th>Provider</th><th>Model</th><th>Requests</th><th>In tokens</th><th>Out tokens</th><th>Cost</th><th>Credits</th></tr></thead>
        <tbody>{d.breakdown.map((r: any) => <tr><td>{name(r.staff_id)}</td><td>{r.provider}</td><td>{r.model}</td><td>{r.requests}</td><td>{r.input_tokens?.toLocaleString()}</td><td>{r.output_tokens?.toLocaleString()}</td><td>{usd(r.cost_usd)}</td><td>{Math.round(r.credits)}</td></tr>)}</tbody>
      </table>
      <h3 style="margin-top:22px">Recent calls</h3>
      <table class="grid">
        <thead><tr><th>Time</th><th>Staff</th><th>Task</th><th>Provider</th><th>Model</th><th>Tier</th><th>Why</th><th>In</th><th>Out</th><th>Cost</th></tr></thead>
        <tbody>{d.recent.slice(0, 80).map((r: any) => <tr><td>{new Date(r.ts).toLocaleTimeString()}</td><td>{name(r.staff_id)}</td><td><code>{(r.chat_id ?? "").slice(-6)}</code></td><td>{r.provider}</td><td>{r.model}</td><td>{r.tier ?? ""}</td><td class="hint">{r.reason ?? ""}</td><td>{r.input_tokens}</td><td>{r.output_tokens}</td><td>{usd4(r.cost_usd)}</td></tr>)}</tbody>
      </table>
    </div>
  );
}

function PricingAdmin() {
  const [d, setD] = useState<any>(null);
  const [msg, setMsg] = useState("");
  const load = () => api("/admin/pricing").then(setD);
  useEffect(() => {
    load();
  }, []);
  if (!d) return <div class="hint">Loading…</div>;
  const sync = async () => {
    setMsg("Syncing…");
    try {
      await api("/admin/pricing/sync-openrouter", { method: "POST" });
      setMsg("Jev price updated from OpenRouter");
      load();
    } catch (e) {
      setMsg((e as Error).message);
    }
  };
  return (
    <div>
      <p class="hint">What each ledger row is priced with. Anthropic rows use the list price per million tokens (input, output, cache read, cache write). Grsai rows use the prices you enter in Models (Grsai publishes no price API; copy them from grsai.com/dashboard/models). Jev rows store OpenRouter's exact per-decision cost. Images charge fixed credits per model; 1 credit = {d.creditUsd} USD for the ledger.</p>
      <table class="grid">
        <thead><tr><th>Model</th><th>Provider</th><th>Tier</th><th>$ in /M</th><th>$ out /M</th><th>$ cache read /M</th><th>$ cache write /M</th><th>Source</th></tr></thead>
        <tbody>{d.models.map((m: any) => <tr><td>{m.id}</td><td>{m.provider}</td><td>{m.tier}</td><td>{m.inputPerM}</td><td>{m.outputPerM}</td><td>{m.cacheReadPerM ?? "–"}</td><td>{m.cacheWritePerM ?? "–"}</td><td class="hint">{m.source}</td></tr>)}</tbody>
      </table>
      <h3 style="margin-top:20px">Jev (router)</h3>
      {d.jevObserved ? <div>Observed from the ledger: {d.jevObserved.decisions} decisions, {usd4(d.jevObserved.costUsd)} total, ≈ {usd4(d.jevObserved.perDecision)} per decision (${d.jevObserved.promptPerM.toFixed(3)} per million input tokens, output free). Every row stores OpenRouter's exact charge.</div> : <div class="hint">No Jev decisions yet (add an OpenRouter key in Settings).</div>}
      {d.jev && d.jev.promptPerM >= 0 && <div class="hint">OpenRouter list price: ${d.jev.promptPerM.toFixed(3)} per million input tokens (synced {new Date(d.jev.syncedAt).toLocaleString()})</div>}
      <div style="margin-top:8px;display:flex;gap:8px;align-items:center"><button class="btn small" onClick={sync}>Sync list price from OpenRouter</button><span class="hint">{msg || "Jev decision models are not always in OpenRouter's public list; the observed figures above are authoritative."}</span></div>
      <h3 style="margin-top:20px">Images (credits per image)</h3>
      <table class="grid"><tbody>{Object.entries(d.images.table).map(([id, cr]) => <tr><td>{id}</td><td>{String(cr)} credits ≈ {usd4(Number(cr) * d.creditUsd)}</td></tr>)}</tbody></table>
    </div>
  );
}

function SkillsAdmin({ me }: { me: Me }) {
  const [skills, setSkills] = useState(me.skills);
  const [msg, setMsg] = useState("");
  const reload = () => api<Me>("/me").then((m) => setSkills(m.skills));
  const upload = async (e: Event) => {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    setMsg("Uploading…");
    const fd = new FormData();
    fd.append("file", file);
    const r = await fetch("/api/admin/skills/upload", { method: "POST", body: fd });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) setMsg(j.error || `HTTP ${r.status}`);
    else setMsg(`Installed: ${j.installed.join(", ") || "none"}${j.ignored?.length ? ` · ignored (no SKILL.md): ${j.ignored.join(", ")}` : ""}${j.skipped?.length ? ` · skipped large files: ${j.skipped.length}` : ""}`);
    input.value = "";
    reload();
  };
  const del = async (name: string) => {
    if (!confirm(`Remove skill ${name}?`)) return;
    await api(`/admin/skills/${encodeURIComponent(name)}`, { method: "DELETE" });
    reload();
  };
  return (
    <div>
      <p class="hint">Upload a .zip that contains one folder per skill, each with a <code>SKILL.md</code> (the same format as Claude Code skills, e.g. zip your <code>whatsnext-website</code>, <code>xelent-sites-porting</code>, <code>scroll-craft</code> folders). Every staff member's desktop app syncs them before each turn. Files over 900 KB are skipped; never put secrets in a skill.</p>
      <div style="display:flex;gap:10px;align-items:center;margin-bottom:14px"><input type="file" accept=".zip" onChange={upload} /><span class="hint">{msg}</span></div>
      {skills.length === 0 ? <div class="hint">No skills installed yet.</div> : (
        <table class="grid"><thead><tr><th>Skill</th><th></th></tr></thead><tbody>{skills.map((s) => <tr><td><code>{s.name}</code></td><td><button class="btn small danger" onClick={() => del(s.name)}>remove</button></td></tr>)}</tbody></table>
      )}
    </div>
  );
}


// ---------------- Xelent Sites (desktop): the staff member's own platform connection
type PlatformStatus = { connected: boolean; kind?: "staff" | "tenant" | "owner"; connectedAt?: number; scope?: string[]; sites?: Array<{ site_id: string; site_name: string; site_status: string; organization_name?: string }>; sitesCheckedAt?: number; toolCount?: number; error?: string };

function SitesPanel() {
  const [st, setSt] = useState<PlatformStatus | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = (refresh: boolean) => api<PlatformStatus>(`/platform${refresh ? "?refresh=1" : ""}`).then((r) => { setSt(r); setErr(r.error ?? ""); }).catch((e) => setErr(e.message));
  useEffect(() => {
    load(true);
  }, []);
  const connect = async () => {
    setBusy(true);
    setErr("");
    try {
      setSt(await api<PlatformStatus>("/platform/connect", { method: "POST", body: JSON.stringify(code.trim() ? { code: code.trim() } : {}) }));
      setCode("");
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const disconnect = async () => {
    if (!confirm("Disconnect Xelent Sites on this computer? The agent loses access to your websites until you paste a new code.")) return;
    setSt(await api<PlatformStatus>("/platform/disconnect", { method: "POST" }));
  };
  if (!st) return <div class="admin"><div class="hint">Loading…</div></div>;
  const canWrite = st.scope?.includes("platform:sites:write");
  return (
    <div class="admin" style="max-width:760px">
      <h2>Xelent Sites</h2>
      <p class="hint" style="margin:-6px 0 18px">{st.kind === "owner" ? "Connected as the platform owner: the agent can reach every website on Xelent Sites. Anything that goes live or changes the platform asks you first." : st.kind === "tenant" ? "Your website is connected through your Xelent Sites account: the agent can edit pages and blog posts, preview, and publish when you say so. The connection is stored encrypted on this computer." : "Connect the agent to the company's website platform so it can list, edit, upload and preview the websites the owner assigned to you. Your connection is yours: it is stored encrypted on this computer and the platform checks every action against your access."}</p>
      {st.connected ? (
        <div>
          <div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;margin-bottom:14px">
            <span style="color:var(--ok, #4ade80);font-weight:600">● Connected</span>
            <span class="hint">since {new Date(st.connectedAt ?? 0).toLocaleString()} · {canWrite ? "edit + upload" : "read only"} · publishing needs the owner's "may publish" right</span>
            <button class="btn small" onClick={() => load(true)}>Refresh</button>
            <button class="btn small danger" onClick={disconnect}>Disconnect</button>
          </div>
          <h3 style="margin:0 0 8px">Websites you can reach ({st.sites?.length ?? 0})</h3>
          {st.sites && st.sites.length ? (
            <table class="grid"><thead><tr><th>Website</th><th>Id</th><th>Status</th></tr></thead><tbody>{st.sites.map((s) => <tr><td>{s.site_name}</td><td><code>{s.site_id}</code></td><td>{s.site_status}</td></tr>)}</tbody></table>
          ) : (
            <div class="hint">No websites are assigned to you yet. Ask the owner to assign one in the platform's Staff page.</div>
          )}
          <p class="hint" style="margin-top:14px">Checked {st.sitesCheckedAt ? new Date(st.sitesCheckedAt).toLocaleTimeString() : "never"}. Try in a chat: "list my websites" or "upload sites/acme to site_acme and show me the preview".</p>
        </div>
      ) : (
        <div>
          <p class="hint" style="max-width:560px">One click: the company cloud confirms you are platform staff and connects this computer to the websites the owner assigned to you. No code to copy.</p>
          <button class="btn primary" onClick={connect} disabled={busy}>{busy ? "Connecting…" : "Connect Xelent Sites"}</button>
          <details style="margin-top:18px;max-width:520px">
            <summary class="hint" style="cursor:pointer">Have a connection code instead?</summary>
            <div class="field" style="margin-top:8px">
              <input class="input" type="password" autocomplete="one-time-code" placeholder="43-character one-time code" value={code} onInput={(e) => setCode((e.target as HTMLInputElement).value)} onKeyDown={(e) => { if (e.key === "Enter" && code.trim()) connect(); }} />
              <span class="hint">From the owner or your Staff workspace at sites.ayzelify.com. Works once; paste it straight away.</span>
            </div>
            <button class="btn small" onClick={connect} disabled={busy || !code.trim()}>Connect with this code</button>
          </details>
        </div>
      )}
      {err && <div class="error" style="margin-top:12px">{err}</div>}
    </div>
  );
}

// ---------------- Shell
type LiveSpend = { today: { cost_usd: number; requests: number }; month: { cost_usd: number; credits: number; pkr?: number }; team: { cost_usd: number }; budget: { staffBudget: number | null; teamBudget: number; creditsBudget?: number } };

function UpdateBadge() {
  const [st, setSt] = useState<any>(null);
  const load = (check = false) => api(`/update${check ? "?check=1" : ""}`).then(setSt).catch(() => {});
  useEffect(() => {
    load(true);
    const t = setInterval(() => load(false), 60000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (st && (st.state === "downloading" || st.state === "installing")) {
      const t = setTimeout(() => load(false), 1000);
      return () => clearTimeout(t);
    }
  }, [st]);
  if (!st || !st.available) return null;
  const go = async () => {
    if (!confirm(`Update Xelent Agent ${st.current} → ${st.available.version}? The app will restart.${st.available.notes ? "\n\n" + st.available.notes : ""}`)) return;
    setSt(await api("/update/install", { method: "POST" }));
  };
  return (
    <div class="update">
      {st.state === "downloading" ? `Downloading update… ${st.progress}%` : st.state === "installing" ? "Installing, the app will restart…" : st.state === "error" ? <span><span class="error">Update failed: {st.error}</span> <button class="btn small" onClick={go}>Try again</button> <a href="https://agent.ayzelify.com/downloads" target="_blank" rel="noreferrer">Download</a></span> : <button class="btn primary small" onClick={go}>Update to {st.available.version}</button>}
    </div>
  );
}

function Shell({ me, refresh, env }: { me: Me; refresh: () => void; env: EnvInfo }) {
  const [chats, setChats] = useState<Chat[]>([]);
  const [projects, setProjects] = useState<string[]>([]);
  const [spend, setSpend] = useState<LiveSpend | null>(null);
  const loadSpend = () => api<LiveSpend>("/usage/live").then(setSpend).catch(() => {});
  useEffect(() => {
    loadSpend();
    const t = setInterval(loadSpend, 20000);
    return () => clearInterval(t);
  }, []);
  const [current, setCurrent] = useState<Chat | null>(null);
  const [view, setView] = useState<"chat" | "admin" | "sites" | "start" | "inbox" | "team" | "inquiries">("chat");
  const newInquiries = useNewInquiryCount(env.desktop);
  const [autoSend, setAutoSend] = useState<{ chatId: string; text: string } | null>(null);
  const onboarding = useOnboarding(me.staff.role, env.desktop);
  useEffect(() => {
    if (onboarding.data?.show && onboarding.data.firstTime) setView("start");
  }, [onboarding.data?.firstTime]);
  const closeStart = () => {
    if (onboarding.data?.firstTime) api("/onboarding/seen", { method: "POST" }).catch(() => {});
    setView("chat");
  };
  const startPrompt = async (text: string) => {
    if (onboarding.data?.firstTime) api("/onboarding/seen", { method: "POST" }).catch(() => {});
    const r = await api("/chats", { method: "POST", body: JSON.stringify({}) });
    await load();
    setAutoSend({ chatId: r.chat.id, text });
    setCurrent(r.chat);
    setView("chat");
    setSideOpen(false);
  };
  const pending = usePendingCount(me.staff.role === "admin");
  const [sideOpen, setSideOpen] = useState(false);
  const [runningIds, setRunningIds] = useState<string[]>([]);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const load = async () => {
    const r = await api("/chats");
    setChats(r.chats);
    if (env.desktop) api("/projects").then((p) => setProjects(p.projects)).catch(() => {});
    setRunningIds(r.running ?? []);
    return r.chats as Chat[];
  };
  useEffect(() => {
    const t = setInterval(() => { if (runningIds.length) load(); }, 5000);
    return () => clearInterval(t);
  }, [runningIds.length]);
  useEffect(() => {
    load().then((c) => {
      if (c.length) setCurrent(c[0]);
      else newChat();
    });
  }, []);
  const newChat = async (pick = false) => {
    let cwd: string | undefined;
    if (pick) {
      const p = await api("/folder/pick", { method: "POST" }).catch(() => ({ path: null }));
      if (!p.path) return;
      cwd = p.path;
    }
    const r = await api("/chats", { method: "POST", body: JSON.stringify({ cwd }) });
    await load();
    setCurrent(r.chat);
    setView("chat");
    setSideOpen(false);
  };
  const newProject = async () => {
    const r = await api("/projects", { method: "POST", body: JSON.stringify({}) }).catch((e) => { alert(e.message); return null; });
    if (!r?.path) return;
    const ch = await api("/chats", { method: "POST", body: JSON.stringify({ cwd: r.path }) });
    await load();
    setCurrent(ch.chat);
    setView("chat");
    setSideOpen(false);
  };
  const removeProject = async (dir: string) => {
    if (!confirm(`Remove "${folderName(dir)}" from Projects?\n\nThe folder and its files stay on disk; its chats move to the Chats list.`)) return;
    await api("/projects", { method: "DELETE", body: JSON.stringify({ path: dir }) });
    await load();
  };
  const del = async (c: Chat) => {
    if (!confirm(`Delete "${c.title}"?`)) return;
    await api(`/chats/${c.id}`, { method: "DELETE" });
    const list = await load();
    if (current?.id === c.id) setCurrent(list[0] ?? null);
  };
  const b = me.budget;
  const monthSpent = spend?.month.cost_usd ?? b.staffSpent;
  const teamSpent = spend?.team.cost_usd ?? b.teamSpent;
  const creditsSpent = spend?.month.pkr ?? (b as any).creditsSpent ?? 0;
  const creditsBudget = spend?.budget.creditsBudget ?? (b as any).creditsBudget ?? 0;
  const pct = me.staff.role === "tenant" ? (creditsBudget ? Math.min(100, (creditsSpent / creditsBudget) * 100) : 0) : b.staffBudget ? Math.min(100, (monthSpent / b.staffBudget) * 100) : b.teamBudget ? Math.min(100, (teamSpent / b.teamBudget) * 100) : 0;
  return (
    <div class="shell">
      {me.staff.role === "admin" && <style>{OVERSIGHT_CSS}</style>}
      <aside class={`side${sideOpen ? " open" : ""}`}>
        <div class="top">
          <div class="brand">Xelent <span>Agent</span></div>
          <span style="display:flex;gap:4px"><button class="btn small" title="New chat in the default workspace" onClick={() => newChat(false)}>+ New chat</button></span>
        </div>
        <div class="chats">
          {(() => {
            const row = (c: Chat) => (
              <div class={`chat-item${current?.id === c.id && view === "chat" ? " active" : ""}`} onClick={() => { setCurrent(c); setView("chat"); setSideOpen(false); }}>
                {runningIds.includes(c.id) && <i class="dot run" />}
                <span class="t">{c.title}</span>
                <span class="x" onClick={(e) => { e.stopPropagation(); del(c); }}>✕</span>
              </div>
            );
            const byProject = new Map<string, Chat[]>();
            for (const dir of projects) byProject.set(dir, []);
            const loose: Chat[] = [];
            for (const c of chats) (c.cwd ? (byProject.get(c.cwd) ?? byProject.set(c.cwd, []).get(c.cwd)!) : loose).push(c);
            const newIn = async (cwd: string) => {
              const r = await api("/chats", { method: "POST", body: JSON.stringify({ cwd }) });
              await load();
              setCurrent(r.chat);
              setView("chat");
            };
            return (
              <>
                {env.desktop && (
                  <div class="section" style="display:flex;justify-content:space-between;align-items:center">
                    <span>Projects</span>
                    <button class="btn small" title="Choose a folder on this computer; every chat inside it works on that folder" onClick={newProject}>+ New project</button>
                  </div>
                )}
                {env.desktop && byProject.size === 0 && <div class="hint" style="padding:4px 10px 10px">A project is a folder on this computer (a website, a client, a job). Click "+ New project" to pick one.</div>}
                {[...byProject.entries()].map(([cwd, list]) => (
                  <div class="project">
                    <div class="project__head" title={cwd} onClick={() => setCollapsed({ ...collapsed, [cwd]: !collapsed[cwd] })}>
                      <span>{collapsed[cwd] ? "▸" : "▾"} 📁 {folderName(cwd)}</span>
                      <span style="display:flex;gap:8px">
                        <span class="x" title="New chat in this project" onClick={(e) => { e.stopPropagation(); newIn(cwd); }}>＋</span>
                        <span class="x" title="Remove from Projects (files stay on disk)" onClick={(e) => { e.stopPropagation(); removeProject(cwd); }}>✕</span>
                      </span>
                    </div>
                    {!collapsed[cwd] && list.length === 0 && <div class="hint" style="padding:2px 22px 6px">No chats yet — press ＋</div>}
                    {!collapsed[cwd] && list.map(row)}
                  </div>
                ))}
                <div class="section">Chats</div>
                {loose.length === 0 && <div class="hint" style="padding:2px 10px 8px">Chats outside a project work in the default workspace.</div>}
                {loose.map(row)}
              </>
            );
          })()}
        </div>
        <div class="bottom">
          {env.desktop && <UpdateBadge />}
          <div>{me.staff.name} · {me.staff.role}</div>
          <div class="meter"><i class={pct > 90 ? "err" : pct > 70 ? "warn" : ""} style={`width:${pct}%`} /></div>
          {me.staff.role === "tenant"
            ? <div title="Credits used in this plan period (1 credit = PKR 1)">Credits {Math.round(creditsSpent).toLocaleString()} of {Math.round(creditsBudget).toLocaleString()}{me.billing?.active ? "" : " this month"}<PlanLine billing={me.billing} desktop={env.desktop} /></div>
            : <div title="Live from the gateway ledger, refreshed every 20 s">Today {usd4(spend?.today.cost_usd ?? 0)} · Month {usd4(monthSpent)}{b.staffBudget ? ` of ${usd(b.staffBudget)}` : ""}</div>}
          {me.staff.role === "admin" && <div>Team this month {usd(teamSpent)} of {usd(b.teamBudget)}{b.anthropicExhausted ? " · budget spent" : ""}</div>}
          <div style="margin-top:8px;display:flex;gap:6px;flex-wrap:wrap">
            {onboarding.data?.show && <button class={`btn small${view === "start" ? " primary" : ""}`} onClick={() => { setView("start"); setSideOpen(false); onboarding.reload(); }}>Start here</button>}
            {me.staff.role === "admin" && <button class="btn small" onClick={() => { setView("admin"); setSideOpen(false); }}>Admin</button>}
            {me.staff.role === "admin" && <button class="btn small" title="What staff and website owners are about to make live" onClick={() => { setView("inbox"); setSideOpen(false); }}>Approvals{pending ? <span class="ov-badge">{pending}</span> : null}</button>}
            {me.staff.role === "admin" && <button class="btn small" title="What each person worked on and what it cost" onClick={() => { setView("team"); setSideOpen(false); }}>Team</button>}
            {env.desktop && <button class={`btn small${view === "inquiries" ? " primary" : ""}`} title="Contact and quote requests from your website" onClick={() => { setView("inquiries"); setSideOpen(false); }}>Inquiries{newInquiries ? ` (${newInquiries})` : ""}</button>}
            {env.desktop && <button class="btn small" title="Connect the agent to your websites on Xelent Sites" onClick={() => { setView("sites"); setSideOpen(false); }}>Xelent Sites</button>}
            <button class="btn small" onClick={() => { const cur = prompt("Current password"); const nx = cur && prompt("New password (8+)"); if (cur && nx) api("/me/password", { method: "POST", body: JSON.stringify({ current: cur, next: nx }) }).then(() => alert("Password changed")).catch((e) => alert(e.message)); }}>Password</button>
            <button class="btn small" onClick={() => api("/logout", { method: "POST" }).then(refresh)}>Sign out</button>
          </div>
        </div>
      </aside>
      <main class="main">
        <div class="topbar">
          <button class="btn small menu" onClick={() => setSideOpen(!sideOpen)}>☰</button>
          <span class="title">{view === "admin" ? "Admin" : view === "sites" ? "Xelent Sites" : view === "start" ? "Start here" : view === "inbox" ? "Approvals" : view === "team" ? "Team" : view === "inquiries" ? "Inquiries" : current?.title ?? ""}</span>
          {me.staff.role === "admin" && !me.features.anthropic && <span class="error">No Anthropic key set</span>}
          {!env.desktop && <span>web · chat only</span>}
          {me.staff.role === "admin" && (me.features.images ? <span>images on</span> : <span>images off</span>)}
          {me.staff.role === "admin" && <span>{me.features.jev ? "Jev routing" : "heuristic routing"}</span>}
        </div>
        {me.billing?.required && view !== "sites" ? <SubscribeScreen billing={me.billing} desktop={env.desktop} refresh={refresh} /> : view === "start" && onboarding.data?.show ? <Onboarding data={onboarding.data} name={me.staff.name} onStart={startPrompt} onClose={closeStart} /> : view === "inbox" ? <InboxView /> : view === "team" ? <TeamView /> : view === "admin" ? <Admin me={me} refresh={refresh} /> : view === "sites" ? <SitesPanel /> : view === "inquiries" ? <InquiriesPanel onDraft={startPrompt} /> : current ? <ChatView key={current.id} env={env} me={me} chat={current} autoSend={autoSend?.chatId === current.id ? autoSend.text : undefined} onAutoSent={() => setAutoSend(null)} onTurnEnd={() => { loadSpend(); load(); }} onTitle={(t) => setChats((p) => p.map((c) => (c.id === current.id ? { ...c, title: t } : c)))} /> : null}
      </main>
    </div>
  );
}

function App() {
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [env, setEnv] = useState<EnvInfo | null>(null);
  const refresh = () => api<Me>("/me").then(setMe).catch(() => setMe(null));
  useEffect(() => {
    api<EnvInfo>("/env").then(setEnv).catch(() => setEnv({ desktop: false, cloud: location.origin }));
    refresh();
  }, []);
  if (me === undefined || !env) return <div class="login"><div class="hint">Loading…</div></div>;
  if (me === null) return <Login onDone={refresh} env={env} />;
  return <Shell me={me} refresh={refresh} env={env} />;
}

render(<App />, document.getElementById("app")!);
